import PublicIcon from "@mui/icons-material/Public";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import { Alert, AlertTitle, Box, CircularProgress, Fade, Tooltip } from "@mui/material";
import { debounce as _debounce, isNil } from "lodash-es";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

interface Props {
  isConnected: boolean;
  variant: "icon" | "alert";
  isAlwaysVisible?: boolean;
  hideAfterConnectedMs?: number;
  noConnectionTitle?: ReactNode;
}

/** Displays status of connection (internet, API, SignalR, etc).
 *  When connection restored, is being displayed for a while and then hides.
 */
export function ConnectionStatusIndicator({
  isConnected,
  variant = "icon",
  isAlwaysVisible = false,
  hideAfterConnectedMs = 5000,
  noConnectionTitle,
}: Props) {
  const isPrevConnectedRef = useRef<boolean | undefined>(undefined);

  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);

  const debounce = useCallback(
    _debounce(
      (callback: () => void) => {
        callback();
      },
      1000,
      { leading: false, trailing: true },
    ),
    [],
  );

  const changeStatus = (newConnected: boolean) => {
    const isConnectedComputed =
      newConnected === true && (isPrevConnectedRef.current === false || isAlwaysVisible);
    const isDisconnectedComputed =
      newConnected === false &&
      (isPrevConnectedRef.current === true || isNil(isPrevConnectedRef.current) || isAlwaysVisible);

    // console.log(9.3, {
    //   newConnected,
    //   prevConnected: isPrevConnectedRef.current,
    //   isConnectedComputed,
    //   isDisconnectedComputed,
    // });

    if (isConnectedComputed || isDisconnectedComputed) {
      const isAutoHide = isConnectedComputed && !isAlwaysVisible;

      setIsWarningVisible(isDisconnectedComputed);
      setIsSuccessVisible(isConnectedComputed);

      if (isAutoHide) {
        setTimeout(() => !isAlwaysVisible && setIsSuccessVisible(false), hideAfterConnectedMs);
      }
    } else {
      setIsWarningVisible(false);
      setIsSuccessVisible(false);
    }

    isPrevConnectedRef.current = newConnected;
  };

  useEffect(() => {
    debounce(() => changeStatus(isConnected));
  }, [isConnected]);

  if (variant === "icon") {
    return (
      <>
        {isWarningVisible && (
          <Tooltip title={noConnectionTitle || "Connection lost!"}>
            <PublicOffIcon color='warning' />
          </Tooltip>
        )}
        {isSuccessVisible && (
          <Tooltip title='Connected'>
            <PublicIcon color='success' />
          </Tooltip>
        )}
      </>
    );
  }

  if (variant === "alert") {
    return (
      <>
        {isWarningVisible && (
          <Fade in={isWarningVisible}>
            <Alert severity='warning' icon={<PublicOffIcon fontSize='inherit' />}>
              <AlertTitle>{noConnectionTitle || "Connection lost!"}</AlertTitle>
              <Box>
                Reconnecting <CircularProgress color='secondary' size={10} />
              </Box>
            </Alert>
          </Fade>
        )}
        {isSuccessVisible && (
          <Fade in={isSuccessVisible}>
            <Alert severity='success' icon={<PublicIcon fontSize='inherit' />}>
              Connection restored!
            </Alert>
          </Fade>
        )}
      </>
    );
  }

  return null;
}
